import React, { createContext, useContext, ReactNode, useCallback, useEffect, useState } from 'react';
import { useRealtimeWebSocket } from '@/contexts/RealtimeWebSocketContext';
import { useSiteContext } from '@/contexts/site';

const HEALTH_CHECK_INTERVAL_MS = 60000; // 60s, same as old shared worker

interface HealthContextType {
  isHealthy: boolean;
  isChecking: boolean;
  healthData: any;
  connected: boolean;
  connectionStable: boolean;
  lastHealthCheckTime: number;
  hasPartialIssues: boolean;
  lastResponseTime: number | null;
  checkStatusNow: () => void;
}

const HealthContext = createContext<HealthContextType | null>(null);

interface HealthProviderProps {
  children: ReactNode;
  showToast?: boolean;
}

export const HealthProvider: React.FC<HealthProviderProps> = ({ children }) => {
  // Hooks must be called unconditionally - the provider order in main.tsx ensures this is available
  const wsContext = useRealtimeWebSocket();
  const { activeSite } = useSiteContext();
  
  // Extract values from context
  const subscribe = wsContext.subscribe;
  const connected = wsContext.connected;
  const send = wsContext.send;
  const [healthData, setHealthData] = useState<any>(null);
  const [isHealthy, setIsHealthy] = useState<boolean>(false);
  const [isChecking, setIsChecking] = useState<boolean>(false);
  const [lastHealthCheckTime, setLastHealthCheckTime] = useState<number>(0);
  const [lastResponseTime, setLastResponseTime] = useState<number | null>(null);
  const [hasPartialIssues, setHasPartialIssues] = useState<boolean>(false);
  const [connectionStable, setConnectionStable] = useState<boolean>(false);

  useEffect(() => {
    const unsubscribe = subscribe((msg) => {
      let parsedMsg;
      try {
        parsedMsg = typeof msg === 'string' ? JSON.parse(msg) : msg;
      } catch {
        return;
      }
      if (parsedMsg.type === 'health.check' || parsedMsg.type === 'health.status') {
        const data = parsedMsg.data;
        setHealthData(data);
        setIsHealthy(data.status === 'healthy' || data.status === true);
        setLastHealthCheckTime(Date.now());
        setLastResponseTime(data.websocket_latency || null);
        setIsChecking(false);
        setHasPartialIssues(false); // You can add logic for partial issues here
      }
    });
    return unsubscribe;
  }, [subscribe]);

  const checkStatusNow = useCallback(() => {
    setIsChecking(true);
    const healthCheckMessage = {
      action: 'health.check',
      data: { site_id: activeSite?.id },
      ts: Date.now()
    };
    send(JSON.stringify(healthCheckMessage));
  }, [send, activeSite?.id]);

  // Trigger health check as soon as WebSocket is connected and we have a site (replaces old worker subscribe_health)
  useEffect(() => {
    if (connected && activeSite?.id) {
      checkStatusNow();
    }
  }, [connected, activeSite?.id, checkStatusNow]);

  // Periodic health check when connected (replaces old worker's 60s interval)
  useEffect(() => {
    if (!connected || !activeSite?.id) return;
    const intervalId = setInterval(checkStatusNow, HEALTH_CHECK_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [connected, activeSite?.id, checkStatusNow]);

  // Use useMemo to ensure stable context value in React 19
  const contextValue: HealthContextType = React.useMemo(() => ({
    isHealthy,
    isChecking,
    healthData,
    connected,
    connectionStable,
    lastHealthCheckTime,
    hasPartialIssues,
    lastResponseTime,
    checkStatusNow,
  }), [
    isHealthy,
    isChecking,
    healthData,
    connected,
    connectionStable,
    lastHealthCheckTime,
    hasPartialIssues,
    lastResponseTime,
    checkStatusNow,
  ]);

  return (
    <HealthContext.Provider value={contextValue}>
      {children}
    </HealthContext.Provider>
  );
};

export const useHealth = (): HealthContextType => {
  const context = useContext(HealthContext);
  if (!context) {
    // Warn if the HealthProvider is missing
    if (typeof window !== 'undefined' && window.console) {
      console.warn('useHealth called outside of HealthProvider. Returning default values.');
    }
    return {
      isHealthy: false,
      isChecking: false,
      healthData: null,
      connected: false,
      connectionStable: false,
      lastHealthCheckTime: 0,
      hasPartialIssues: false,
      lastResponseTime: null,
      checkStatusNow: () => {},
    };
  }
  return context;
}; 